import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, RefreshCw, MapPin, ExternalLink, Github, Users, Info,
  BookmarkPlus, BookmarkCheck, Globe, Bookmark, Eye, FileText, Layers, Lock,
  LockOpen, CheckCircle2, ChevronDown, ChevronUp, ListFilter, Plus, Star, X,
  UserSearch, Phone,
} from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { cn } from '@/lib/utils';
import {
  getRecommendedCandidates,
  saveSourcedCandidate,
  removeSourcedCandidate,
  listSourcedCandidates,
  markCandidateViewed,
  unlockTalentPoolCandidate,
  saveMustKeywords,
} from './sourcingApi';
import { ResumeViewerDialog } from './ResumePreview';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';

const SOURCE_META = {
  internal_pool: { label: 'Your applicant', Icon: Users, cls: 'bg-violet-100 text-violet-700' },
  talent_pool: { label: 'Talent pool', Icon: Layers, cls: 'bg-teal-100 text-teal-700' },
  github: { label: 'GitHub', Icon: Github, cls: 'bg-slate-200 text-slate-800' },
  web_search: { label: 'Web', Icon: Globe, cls: 'bg-sky-100 text-sky-700' },
  pdl: { label: 'External', Icon: UserSearch, cls: 'bg-indigo-100 text-indigo-700' },
};

// Sources whose profiles are bought from a data provider (shown as "External").
const EXTERNAL_SOURCES = new Set(['pdl']);

// What to tell the recruiter after an external search that bought nothing.
// Keyed by the server's `external.status`; anything unlisted is a provider or
// network failure.
const EXTERNAL_NOTICES = {
  already_searched: 'External profiles for this search are already in the list. Change the keywords or the job description to search again.',
  cap_reached: 'The external search limit for this month has been reached.',
  criteria_too_thin: 'This job needs a clear title before an external search can run.',
  unverified_identity: 'Please sign in again to run an external search.',
  unavailable: 'External search is not switched on for this account.',
};
const EXTERNAL_FAILED = 'External search is unavailable right now. Please try again later.';

const shortDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const ROLE_FIT_META = {
  same: { label: 'Same role', cls: 'bg-emerald-100 text-emerald-700' },
  adjacent: { label: 'Adjacent role', cls: 'bg-amber-100 text-amber-700' },
};

const initialsFor = (name) => {
  const parts = String(name || '?').trim().split(/[.\s@_-]+/).filter(Boolean);
  const seed = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : (parts[0] || '?').slice(0, 2);
  return seed.toUpperCase();
};

const scoreColor = (s) => {
  if (s >= 75) return 'text-emerald-600';
  if (s >= 50) return 'text-amber-600';
  return 'text-slate-500';
};

const keyOf = (c) => c.dedupe_key || c.email || c.name;

// Mirrors the server cap (services/sourcing/keywords.py MAX_KEYWORDS); the
// server is the authority and rejects anything over it.
const MAX_MUST_KEYWORDS = 5;

// A shared talent-pool lead is keyed by an opaque `tp:<id>` (never the email),
// which is also how a saved row is recognised after a reload.
const isTalentPool = (c) =>
  c?.pool_origin === 'talent_pool' || String(c?.dedupe_key || '').startsWith('tp:');

const profileAge = (days) => {
  if (typeof days !== 'number' || days < 0) return '';
  if (days < 31) return 'profile updated this month';
  const months = Math.round(days / 30);
  if (months < 12) return `profile from ${months} mo ago`;
  const years = Math.floor(months / 12);
  return `profile from ${years} yr${years === 1 ? '' : 's'} ago`;
};

// In-memory cache keyed by jobId so switching tabs (or navigating away and
// back) restores the last result instantly instead of re-fetching and flashing
// the "Sourcing candidates…" spinner every time. Manual Refresh still re-runs,
// and a full page reload clears it. Lives at module scope to survive the
// component being unmounted/remounted by the Tabs.
const recCache = new Map(); // jobId -> { data, savedKeys: string[], viewedKeys: string[] }

/**
 * Recommended-candidates panel (the "Recommended" tab on the JD applications
 * page). Two sub-views: auto-sourced **Recommendations** and the recruiter's
 * **Saved** sourced leads for this job. Self-contained: fetches, scores,
 * saves/removes, and shows loading/empty states.
 *
 * Recommendations come from three places, each labelled on its card:
 *   - "Your applicant"  — this company's own candidates who fit this JD
 *   - "Talent pool"     — the shared SearchJobAI pool. Contact details stay
 *                         locked (surname initial, no email/résumé) until the
 *                         recruiter unlocks the candidate, which also saves
 *                         them to this job.
 *   - GitHub / Web      — public profiles
 *   - "External"        — professional profiles bought from a data provider.
 *                         Never fetched automatically: the recruiter asks for
 *                         them with "Search external profiles" (and confirms,
 *                         because it uses paid credits). The button only shows
 *                         when the server says the source is switched on.
 */
const RecommendedCandidates = ({ jobId }) => {
  const { can } = useCompanyAccess();
  const canSaveCandidates = can('candidates.add');
  const [subView, setSubView] = useState('recommendations'); // 'recommendations' | 'saved'

  const cached = recCache.get(jobId); // seed the first paint from a prior visit
  const [data, setData] = useState(cached?.data || null);
  const [isLoading, setIsLoading] = useState(!cached);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [savedKeys, setSavedKeys] = useState(() => new Set(cached?.savedKeys || []));
  const [savingKey, setSavingKey] = useState('');
  const [viewedKeys, setViewedKeys] = useState(() => new Set(cached?.viewedKeys || []));
  const [savedList, setSavedList] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [showNearMisses, setShowNearMisses] = useState(false);
  // "Must include" keywords: typed by the recruiter, saved with the job.
  const [keywordInput, setKeywordInput] = useState('');
  const [keywordSaving, setKeywordSaving] = useState(false);
  // Paid external search: a confirm step, then the request.
  const [externalConfirm, setExternalConfirm] = useState(false);
  const [externalBusy, setExternalBusy] = useState(false);

  // Set true on every mount, not only at creation: React re-runs effects on
  // Fast Refresh and in StrictMode (cleanup, then mount again). With cleanup
  // alone the flag stayed false afterwards and every response was ignored,
  // leaving the panel on its spinner forever.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Resolves to the response, or null when the request failed.
  const load = useCallback(async ({ refresh = false, external = false } = {}) => {
    if (refresh) setIsRefreshing(true); else setIsLoading(true);
    setError('');
    try {
      const res = await getRecommendedCandidates(jobId, { refresh, external });
      if (mountedRef.current) {
        setData(res);
        setSavedKeys(new Set(res?.saved_keys || []));
        setViewedKeys(new Set(res?.viewed_keys || []));
        if (refresh) setShowNearMisses(false);
      }
      return res;
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not load recommendations.';
      if (mountedRef.current) setError(msg);
      return null;
    } finally {
      if (mountedRef.current) { setIsLoading(false); setIsRefreshing(false); }
    }
  }, [jobId]);

  // Hydrate from the session cache when we have it (instant, no spinner, no
  // refetch); only hit the server the first time this job's tab is opened.
  useEffect(() => {
    const c = recCache.get(jobId);
    if (c) {
      setData(c.data);
      setSavedKeys(new Set(c.savedKeys));
      setViewedKeys(new Set(c.viewedKeys));
      setIsLoading(false);
      return;
    }
    load();
  }, [jobId, load]);

  // Keep the cache in sync so returning restores exactly what you left,
  // including saves/views made this session.
  useEffect(() => {
    if (data) {
      recCache.set(jobId, { data, savedKeys: [...savedKeys], viewedKeys: [...viewedKeys] });
    }
  }, [jobId, data, savedKeys, viewedKeys]);

  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const rows = await listSourcedCandidates(jobId);
      if (!mountedRef.current) return;
      setSavedList(rows);
      setSavedKeys((prev) => {
        const n = new Set(prev);
        rows.forEach((r) => { const k = keyOf(r); if (k) n.add(k); });
        return n;
      });
    } catch (err) {
      if (mountedRef.current) toast.error(err?.response?.data?.error || 'Could not load saved leads.');
    } finally {
      if (mountedRef.current) setSavedLoading(false);
    }
  }, [jobId]);

  useEffect(() => { if (subView === 'saved') loadSaved(); }, [subView, loadSaved]);

  // Swap a locked talent-pool card for its unlocked form, wherever it sits
  // (main list or near-misses), without refetching the whole panel.
  const applyUnlocked = useCallback((key, revealed) => {
    const patch = (list) => (list || []).map((c) => {
      if (keyOf(c) !== key) return c;
      return {
        ...c,
        name: revealed?.name || c.name,
        email: revealed?.email || c.email,
        contact_locked: false,
        signals: { ...(c.signals || {}), resume_url: revealed?.resume_url || c.signals?.resume_url },
      };
    });
    setData((prev) => (prev ? {
      ...prev,
      candidates: patch(prev.candidates),
      near_misses: patch(prev.near_misses),
    } : prev));
  }, []);

  const handleUnlock = useCallback(async (c) => {
    const key = keyOf(c);
    if (!key) return;
    setSavingKey(key);
    try {
      const res = await unlockTalentPoolCandidate(jobId, c);
      if (!mountedRef.current) return;
      applyUnlocked(key, res?.candidate);
      setSavedKeys((prev) => new Set(prev).add(key));
      toast.success('Contact unlocked and saved to this job');
    } catch (err) {
      const reason = err?.response?.data?.reason;
      toast.error(err?.response?.data?.error || 'Could not unlock this candidate.');
      // The candidate left the pool (flag turned off / consent withdrawn):
      // drop the stale card rather than leave a button that can never work.
      if (reason === 'not_visible' && mountedRef.current) {
        setData((prev) => (prev ? {
          ...prev,
          candidates: (prev.candidates || []).filter((x) => keyOf(x) !== key),
          near_misses: (prev.near_misses || []).filter((x) => keyOf(x) !== key),
        } : prev));
      }
    } finally {
      if (mountedRef.current) setSavingKey('');
    }
  }, [jobId, applyUnlocked]);

  const handleSaveToggle = useCallback(async (c) => {
    const key = keyOf(c);
    if (!key) return;
    const isSaved = savedKeys.has(key);
    // A talent-pool lead is saved through the unlock call (the server fills in
    // the contact details); removing it is an ordinary remove.
    if (!isSaved && isTalentPool(c)) {
      await handleUnlock(c);
      return;
    }
    setSavingKey(key);
    try {
      if (isSaved) {
        await removeSourcedCandidate(jobId, key);
        setSavedKeys((prev) => { const n = new Set(prev); n.delete(key); return n; });
        setSavedList((prev) => prev.filter((x) => keyOf(x) !== key));
        toast.success('Removed from saved');
      } else {
        await saveSourcedCandidate(jobId, c);
        setSavedKeys((prev) => new Set(prev).add(key));
        toast.success('Saved to this job');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update saved candidates.');
    } finally {
      if (mountedRef.current) setSavingKey('');
    }
  }, [jobId, savedKeys, handleUnlock]);

  const handleView = useCallback((c) => {
    const key = keyOf(c);
    if (!key || viewedKeys.has(key)) return;
    setViewedKeys((prev) => new Set(prev).add(key));
    markCandidateViewed(jobId, key); // fire-and-forget
  }, [jobId, viewedKeys]);

  // In-app résumé viewer ({ url, name }); the raw S3 link is never linked.
  const [resumeViewer, setResumeViewer] = useState(null);

  const recommendations = data?.candidates || [];
  const nearMisses = data?.near_misses || [];
  const githubSkipped = (data?.sources_skipped || []).includes('github');
  const savedCount = savedKeys.size;
  // Pool sizes and card counts are internal: the page only needs to know
  // WHETHER the whole pool was searched and whether any talent-pool card is
  // on screen, never how many.
  const poolSearched = Boolean(data?.pool?.searched?.total);
  const hasTalentPoolCards = recommendations.some(isTalentPool);
  const mustKeywords = Array.isArray(data?.must_keywords) ? data.must_keywords : [];
  const criticalSkills = Array.isArray(data?.critical_skills) ? data.critical_skills : [];
  const keywordsFull = mustKeywords.length >= MAX_MUST_KEYWORDS;

  // Save the new keyword list for this job, then re-run sourcing so the cards
  // reflect it. The server validates (count, length, characters) and its
  // message is shown as is.
  const updateKeywords = async (next) => {
    setKeywordSaving(true);
    try {
      await saveMustKeywords(jobId, next);
      if (!mountedRef.current) return;
      setKeywordInput('');
      setShowNearMisses(false);
      await load({ refresh: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update the keywords.');
    } finally {
      if (mountedRef.current) setKeywordSaving(false);
    }
  };

  const addKeyword = () => {
    const value = keywordInput.trim();
    if (!value || keywordSaving) return;
    if (mustKeywords.some((k) => k.toLowerCase() === value.toLowerCase())) {
      setKeywordInput('');
      return;
    }
    updateKeywords([...mustKeywords, value]);
  };

  const removeKeyword = (label) => {
    if (keywordSaving) return;
    updateKeywords(mustKeywords.filter((k) => k !== label));
  };

  // The paid external source. `available` comes from the server: the source
  // is switched on AND this caller is allowed to use it.
  const externalAvailable = Boolean(data?.external?.available);
  const externalSearchedOn = shortDate(data?.external?.searched_at);
  // A lasting line about the last external search, so its outcome is not only
  // a pop-up: where the profiles are, or why none is on screen.
  const externalOutcome = (() => {
    if (!externalSearchedOn) return '';
    const isExternal = (c) => EXTERNAL_SOURCES.has(c?.source);
    if (recommendations.some(isExternal)) return 'The profiles found are marked External below.';
    if (nearMisses.some(isExternal)) return 'The profiles found scored below the recommendation bar. See the near-misses.';
    if (data?.external?.fetched === 0) return 'No matching profiles were found.';
    return 'None of the profiles found was a strong enough match to show.';
  })();

  const runExternalSearch = async () => {
    if (externalBusy) return;
    setExternalConfirm(false);
    setExternalBusy(true);
    const res = await load({ refresh: true, external: true });
    if (!mountedRef.current) return;
    setExternalBusy(false);
    if (!res) return; // the request failed; the error is already on screen
    const status = res?.external?.status;
    if (status === 'fetched' || status === 'fetched_not_saved') {
      const isExternal = (c) => EXTERNAL_SOURCES.has(c?.source);
      const recommended = (res.candidates || []).some(isExternal);
      const belowBar = (res.near_misses || []).some(isExternal);
      if (recommended) toast.success('External profiles added. They are marked External in the list.');
      else if (belowBar) toast.info('External profiles were found, but none cleared the recommendation bar. See the near-misses.');
      else toast.info('The external search found no suitable profiles for this job.');
      return;
    }
    if (EXTERNAL_NOTICES[status]) toast.info(EXTERNAL_NOTICES[status]);
    else toast.error(EXTERNAL_FAILED);
  };

  const items = subView === 'saved' ? savedList : recommendations;
  const showLoading = subView === 'saved' ? savedLoading : isLoading;

  const renderCard = (c, idx) => {
    const fromPool = isTalentPool(c);
    const meta = (fromPool ? SOURCE_META.talent_pool : SOURCE_META[c.source])
      || { label: c.source, Icon: Users, cls: 'bg-slate-100 text-slate-700' };
    const SrcIcon = meta.Icon;
    const key = keyOf(c);
    const isSaved = savedKeys.has(key);
    const busy = savingKey === key;
    const locked = Boolean(c.contact_locked);
    const s = c.signals || {};
    const matched = Array.isArray(s.matched_skills) ? s.matched_skills : [];
    const matchedSet = new Set(matched.map((m) => String(m).toLowerCase()));
    const roleFit = ROLE_FIT_META[c.role_fit];
    const criticalHits = Array.isArray(s.critical_hits) ? s.critical_hits : [];
    const criticalSet = new Set(criticalHits.map((m) => String(m).toLowerCase()));
    const keywordHits = Array.isArray(s.keyword_hits) ? s.keyword_hits : [];
    const keywordMissing = Array.isArray(s.keyword_missing) ? s.keyword_missing : [];
    // Critical skills of the JD this profile does not name. The fit score is
    // already lowered for each one; the points themselves are an internal
    // detail and are not shown.
    const criticalMissing = Array.isArray(s.critical_missing) ? s.critical_missing : [];
    return (
      <div key={`${key}-${idx}`}
           className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:border-primary/30 transition-colors">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
          {initialsFor(c.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="truncate text-sm font-semibold text-slate-900">{c.name}</span>
                {locked ? (
                  <span title="Unlock to see the full name, email and résumé"
                        className="inline-flex shrink-0 items-center text-slate-400">
                    <Lock className="h-3 w-3" />
                  </span>
                ) : null}
              </div>
              {c.headline ? <div className="truncate text-xs text-slate-500">{c.headline}</div> : null}
            </div>
            {typeof c.fit_score === 'number' ? (
              <div className="text-right shrink-0">
                <div className={cn('text-lg font-bold leading-none', scoreColor(c.fit_score))}>{c.fit_score}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">fit</div>
              </div>
            ) : null}
          </div>

          {c.fit_reason ? <div className="mt-1 text-xs text-slate-600 italic">“{c.fit_reason}”</div> : null}

          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            <Badge variant="secondary" className={cn('h-5 gap-1 px-1.5 text-[11px]', meta.cls)}>
              <SrcIcon className="h-3 w-3" />{meta.label}
            </Badge>
            {roleFit ? (
              <Badge variant="secondary" className={cn('h-5 px-1.5 text-[11px]', roleFit.cls)}>
                {roleFit.label}
              </Badge>
            ) : null}
            {c.location ? (
              <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-500">
                <MapPin className="h-3 w-3" />{c.location}
              </span>
            ) : null}
            {viewedKeys.has(key) ? (
              <Badge variant="secondary" className="h-5 gap-1 px-1.5 text-[11px] bg-emerald-100 text-emerald-700">
                <Eye className="h-3 w-3" /> Viewed
              </Badge>
            ) : null}
          </div>

          {/* The recruiter's own must-include keywords: found (green) or, on a
              near-miss, missing (red). */}
          {keywordHits.length || keywordMissing.length ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {keywordHits.map((k) => (
                <span key={`hit-${k}`} title="Your must-include keyword appears in this résumé"
                      className="inline-flex items-center gap-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  <CheckCircle2 className="h-3 w-3" />{k}
                </span>
              ))}
              {keywordMissing.map((k) => (
                <span key={`miss-${k}`} title="Your must-include keyword was not found in this résumé"
                      className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-red-200">
                  <X className="h-3 w-3" />Missing: {k}
                </span>
              ))}
            </div>
          ) : null}

          {/* Evidence: which of the JD's must-have skills the résumé names. */}
          {typeof s.must_total === 'number' && s.must_total > 0 ? (
            <div className={cn('mt-1.5 inline-flex items-center gap-1 text-[11px]',
              matched.length ? 'text-emerald-700' : 'text-slate-400')}>
              <CheckCircle2 className="h-3 w-3" />
              {matched.length
                ? `Names ${matched.length} of ${s.must_total} must-have skills`
                : `Names none of the ${s.must_total} must-have skills`}
              {s.critical_total ? ` · ${criticalHits.length} of ${s.critical_total} critical` : ''}
            </div>
          ) : null}

          {criticalMissing.length ? (
            <div className="mt-1 flex items-center gap-1 text-[11px] text-red-600"
                 title="Each critical skill the profile does not name lowers the fit score">
              <X className="h-3 w-3 shrink-0" />
              <span>Missing critical: {criticalMissing.join(', ')}</span>
            </div>
          ) : null}

          {(c.skills || []).length ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {c.skills.slice(0, 6).map((sk, i) => {
                const lower = String(sk).toLowerCase();
                const isCritical = criticalSet.has(lower);
                return (
                  <span key={i}
                        title={isCritical ? 'A critical skill on this JD' : undefined}
                        className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px]',
                          isCritical
                            ? 'bg-primary/10 text-primary font-medium ring-1 ring-primary/30'
                            : matchedSet.has(lower)
                              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                              : 'bg-slate-100 text-slate-600')}>
                    {isCritical ? <Star className="h-2.5 w-2.5" /> : null}{sk}
                  </span>
                );
              })}
            </div>
          ) : null}

          {(() => {
            const bits = [];
            if (s.followers) bits.push(`★ ${s.followers.toLocaleString?.() || s.followers} followers`);
            if (s.public_repos) bits.push(`${s.public_repos} repos`);
            if (s.prior_resume_score) bits.push(`scored ${s.prior_resume_score} here before`);
            const age = profileAge(s.profile_age_days);
            if (age) bits.push(age);
            if (EXTERNAL_SOURCES.has(c.source)) {
              // Exact tenure when known; otherwise the minimum the search required.
              if (typeof s.experience_years === 'number') bits.push(`${Math.round(s.experience_years)} yrs experience`);
              else if (s.experience_at_least) bits.push(`${s.experience_at_least}+ yrs experience`);
              if (!c.email && !s.phone) bits.push('contact via profile link');
            }
            return bits.length ? <div className="mt-1.5 text-[11px] text-slate-400">{bits.join(' · ')}</div> : null;
          })()}

          <div className="mt-2 flex items-center gap-3">
            {c.profile_url ? (
              <a href={c.profile_url} target="_blank" rel="noopener noreferrer"
                 onClick={() => handleView(c)}
                 className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                <ExternalLink className="h-3 w-3" /> View profile
              </a>
            ) : null}
            {!locked && s.resume_url ? (
              <button type="button"
                 onClick={() => { handleView(c); setResumeViewer({ url: s.resume_url, name: c.name || '' }); }}
                 className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                <FileText className="h-3 w-3" /> View résumé
              </button>
            ) : null}
            {s.portfolio_url ? (
              <a href={String(s.portfolio_url).startsWith('http') ? s.portfolio_url : `https://${s.portfolio_url}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
                <Globe className="h-3 w-3" /> Portfolio
              </a>
            ) : null}
            {!locked && c.email ? (
              <a href={`mailto:${c.email}`}
                 className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 truncate max-w-[140px]">
                {c.email}
              </a>
            ) : null}
            {!locked && s.phone ? (
              <a href={`tel:${String(s.phone).replace(/[^+0-9]/g, '')}`}
                 className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
                <Phone className="h-3 w-3" /> {s.phone}
              </a>
            ) : null}
            {locked ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                <Lock className="h-3 w-3" /> Contact locked
              </span>
            ) : null}

            {!canSaveCandidates ? null : locked ? (
              <button
                type="button"
                onClick={() => handleUnlock(c)}
                disabled={busy}
                title="Reveals the full name, email and résumé, and saves the candidate to this job"
                className="ml-auto inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LockOpen className="h-3 w-3" />}
                Unlock contact
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleSaveToggle(c)}
                disabled={busy}
                className={cn(
                  'ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                  isSaved ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-primary/10 text-primary hover:bg-primary/20'
                )}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : isSaved ? <BookmarkCheck className="h-3 w-3" /> : <BookmarkPlus className="h-3 w-3" />}
                {isSaved ? 'Saved' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const nearMissToggle = nearMisses.length ? (
    <button type="button" onClick={() => setShowNearMisses((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
      {showNearMisses ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      {showNearMisses ? 'Hide near-misses' : `Show ${nearMisses.length} near-miss${nearMisses.length === 1 ? '' : 'es'}`}
    </button>
  ) : null;

  const nearMissSection = showNearMisses && nearMisses.length ? (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
        <Info className="h-3.5 w-3.5 shrink-0" />
        Near-misses scored below the recommendation bar. They are shown for reference, not as recommendations.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-90">
        {nearMisses.map(renderCard)}
      </div>
    </div>
  ) : null;

  return (
    <Card className="border-2 border-primary/20 bg-primary/[0.02]">
      <ResumeViewerDialog
        url={resumeViewer?.url || null}
        candidateName={resumeViewer?.name || ''}
        onClose={() => setResumeViewer(null)}
      />
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <Heading level="h3" className="text-base font-semibold">Recommended candidates</Heading>
              <Text size="xs" variant="muted">
                Auto-sourced and scored against this job — from your applicants, the SearchJobAI talent pool and public profiles. Save the good ones.
              </Text>
            </div>
          </div>
          {subView === 'recommendations' ? (
            <Button variant="outline" size="sm" onClick={() => load({ refresh: true })}
                    disabled={isLoading || isRefreshing} className="gap-1.5 h-8" title="Re-run sourcing">
              <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
          ) : null}
        </div>

        {/* Sub-view toggle */}
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-sm">
          <button type="button" onClick={() => setSubView('recommendations')}
            className={cn('rounded-md px-3 py-1 transition-colors',
              subView === 'recommendations' ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-500 hover:text-slate-700')}>
            Recommendations
          </button>
          <button type="button" onClick={() => setSubView('saved')}
            className={cn('inline-flex items-center gap-1 rounded-md px-3 py-1 transition-colors',
              subView === 'saved' ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-500 hover:text-slate-700')}>
            <Bookmark className="h-3.5 w-3.5" /> Saved{savedCount ? ` (${savedCount})` : ''}
          </button>
        </div>

        {/* Must-include keywords + the JD's critical skills (recommendations only) */}
        {subView === 'recommendations' && data && !error ? (
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700">
                <ListFilter className="h-3.5 w-3.5" /> Must include
              </span>
              {mustKeywords.map((k) => (
                <span key={k}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 py-0.5 pl-2 pr-1 text-xs font-medium text-white">
                  {k}
                  <button type="button" onClick={() => removeKeyword(k)} disabled={keywordSaving || !canSaveCandidates}
                          aria-label={`Remove ${k}`}
                          className="rounded p-0.5 hover:bg-white/20 disabled:opacity-50">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {canSaveCandidates && !keywordsFull ? (
                <form className="inline-flex items-center gap-1"
                      onSubmit={(e) => { e.preventDefault(); addKeyword(); }}>
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    disabled={keywordSaving}
                    maxLength={120}
                    aria-label="Add a must-include keyword"
                    placeholder={mustKeywords.length ? 'Add another keyword' : 'Type a keyword and press Enter'}
                    className="h-7 w-52 rounded-md border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50"
                  />
                  <button type="submit" disabled={keywordSaving || !keywordInput.trim()}
                          className="inline-flex h-7 items-center gap-1 rounded-md bg-primary/10 px-2 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-50">
                    {keywordSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    Add
                  </button>
                </form>
              ) : (
                <span className="text-[11px] text-slate-400">Maximum reached. Remove one to add another.</span>
              )}
            </div>
            <Text size="xs" variant="muted">
              Only candidates whose résumé names every keyword are recommended; the rest appear under near-misses.
              For alternatives, separate them with a slash and spaces: first name / second name.
            </Text>
            {criticalSkills.length ? (
              <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2">
                <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                  <Star className="h-3.5 w-3.5" /> Critical skills from the JD
                </span>
                {criticalSkills.map((k) => (
                  <span key={k} className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary ring-1 ring-primary/30">
                    {k}
                  </span>
                ))}
                <span className="text-[11px] text-slate-400">weigh most in ranking</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Paid external search (only when the server says it is switched on) */}
        {subView === 'recommendations' && data && !error && externalAvailable ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2">
            <div className="flex items-start gap-2 text-xs text-indigo-900">
              <UserSearch className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Need more candidates? Search external professional profiles for people who have not applied to you.
                {externalSearchedOn ? ` Last searched ${externalSearchedOn}. ${externalOutcome}` : ''}
                <span className="block text-[11px] text-indigo-700/80">
                  Uses paid credits. Repeating an unchanged search costs nothing.
                </span>
              </span>
            </div>
            {externalConfirm ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-indigo-900">Use paid credits for this job?</span>
                <Button size="sm" className="h-7" onClick={runExternalSearch}>Yes, search</Button>
                <Button size="sm" variant="outline" className="h-7" onClick={() => setExternalConfirm(false)}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-8 gap-1.5"
                      disabled={!can('sourcing.external') || externalBusy || isLoading || isRefreshing || keywordSaving}
                      onClick={() => setExternalConfirm(true)}>
                {externalBusy
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <UserSearch className="h-3.5 w-3.5" />}
                {externalBusy ? 'Searching…' : 'Search external profiles'}
              </Button>
            )}
          </div>
        ) : null}

        {/* GitHub-not-configured hint (recommendations only) */}
        {subView === 'recommendations' && githubSkipped ? (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
            <Info className="h-3.5 w-3.5 shrink-0" />
            GitHub sourcing is off — add a free <span className="font-mono">GITHUB_TOKEN</span> on the server to source developers from GitHub too.
          </div>
        ) : null}

        {/* Talent-pool explainer (only when such cards are on screen) */}
        {subView === 'recommendations' && !showLoading && hasTalentPoolCards ? (
          <div className="flex items-start gap-2 rounded-md border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs text-teal-800">
            <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Cards marked Talent pool are from the SearchJobAI talent pool.
              Unlock a candidate to see their full name, email and résumé — it also saves them to this job.
            </span>
          </div>
        ) : null}

        {/* Body */}
        {showLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary mb-3" />
            <Text size="sm" className="font-medium">
              {subView === 'saved' ? 'Loading saved leads…' : 'Sourcing candidates…'}
            </Text>
            {subView === 'recommendations' ? (
              <Text size="xs" variant="muted">Matching the JD against your applicants, the talent pool and public profiles.</Text>
            ) : null}
          </div>
        ) : subView === 'recommendations' && error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">{error}</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center space-y-3">
            <div>
              {subView === 'saved' ? <Bookmark className="mx-auto mb-2 h-6 w-6 text-slate-300" /> : <Users className="mx-auto mb-2 h-6 w-6 text-slate-300" />}
              <Text size="sm" className="font-medium">
                {subView === 'saved'
                  ? 'No saved leads yet'
                  : mustKeywords.length
                    ? 'No candidates name all your must-include keywords'
                    : poolSearched
                      ? 'No strong matches for this job'
                      : 'No recommendations yet'}
              </Text>
              <Text size="xs" variant="muted">
                {subView === 'saved'
                  ? 'Save candidates from Recommendations to build your shortlist for this job.'
                  : mustKeywords.length
                    ? (data?.keyword_blocker
                      ? `Most otherwise-suitable candidates are missing “${data.keyword_blocker}”. Remove it above to see them, or open the near-misses.`
                      : 'The candidates who name every keyword did not clear the quality bar for this job. Remove a keyword above, or open the near-misses.')
                    : poolSearched
                      ? 'We searched every candidate you can see for this JD and none cleared the quality bar. We would rather show nothing than a weak match.'
                      : "We couldn't find matching candidates in your pool or public sources for this JD."}
              </Text>
            </div>
            {subView === 'recommendations' ? (
              <div className="flex flex-col items-center gap-2">
                {nearMissToggle}
              </div>
            ) : null}
            {subView === 'recommendations' ? <div className="text-left">{nearMissSection}</div> : null}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map(renderCard)}
            </div>
            {subView === 'recommendations' ? (
              <>
                {nearMissToggle ? <div className="flex justify-center">{nearMissToggle}</div> : null}
                {nearMissSection}
              </>
            ) : null}
          </div>
        )}

        {/* Internal details (cache state, pool sizes, source names) are
            deliberately not shown to recruiters. Only the saved view keeps a
            simple count. */}
        {!showLoading && items.length > 0 && subView === 'saved' ? (
          <Text size="xs" variant="muted" className="text-center">
            {`${items.length} saved lead${items.length === 1 ? '' : 's'}`}
          </Text>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default RecommendedCandidates;
